import React, { useState, useEffect } from 'react'
import Plot from 'react-plotly.js'
import { computeSelectedWeekForSeason, getWeekFromWeek, getYearFromWeek, SeasonRange, seasonReferenceYear, toAbsoluteUrl } from '../../../../../../_metronic/helpers'
import axios from 'axios'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useSelectedWeek } from '../../../../../contexts/SelectedWeekContext'
import SourceLink from '../../component/chartLinkData'
// Sample data structure (modify as needed)
interface WeekData {
  week: string
  tenthPercentileThreshold: number | null
  fifthPercentileThreshold: number | null
  firstPercentileThreshold: number | null
  weeklyAnomalyScore: number | null
}
type RespiratorySyndromesGraphProps = {
  season: SeasonRange,
  onClick: () => void
}
const AnomalyDetectionGraph: React.FC<RespiratorySyndromesGraphProps> = ({ season, onClick }) => {
  // Sample data (replace with actual data or fetch from API)
  const { selectedWeek, setSelectedWeek } = useSelectedWeek('2023-W40')
  const [data, setData] = useState<WeekData[]>([])
  const [showArrow, setShowArrow] = useState<boolean>(false)
  const [missingData, setMissingData] = useState<WeekData[]>([])

  const [seasonSelectedWeek, setSeasonSelectedWeek] = useState<string | null>(null)
  const startYear = getYearFromWeek(season.startWeek)
  const endYear = getYearFromWeek(season.endWeek)
  const startWeekNumber = getWeekFromWeek(season.startWeek) + 1

  const API_URL = process.env.REACT_APP_API_URL
  function addMissingWeek(data: WeekData[]): WeekData[] {
    const startWeek = season.startWeek // First week in the data
    // const endWeek = data[data.length - 1].week // Last week in the data
    const endWeek = season.endWeek

    const existingWeeks = new Set(data.map((item) => item.week)) // Set to store existing weeks
    const newData: WeekData[] = []
    const missingData: WeekData[] = []
    let currentWeek = startWeek

    while (currentWeek !== endWeek) {
      // Move to the next week
      const [year, weekNum] = currentWeek.split('-W')
      const weekNumber = parseInt(weekNum)
      if (weekNumber < 52) {
        currentWeek = `${year}-W${(weekNumber + 1).toString()}`
      } else {
        currentWeek = `${parseInt(year) + 1}-W1`
      }
      if (!existingWeeks.has(currentWeek)) {
        newData.push({
          week: currentWeek,
          tenthPercentileThreshold: null,
          fifthPercentileThreshold: null,
          firstPercentileThreshold: null,
          weeklyAnomalyScore: null,
        })
        missingData.push({
          week: currentWeek,
          tenthPercentileThreshold: null,
          fifthPercentileThreshold: null,
          firstPercentileThreshold: null,
          weeklyAnomalyScore: null,
        })
      }
    }

    // Find the index to insert the new week
    let insertionIndex = 0
    for (let i = 0; i < data.length; i++) {
      if (data[i].week < `${startYear}-W52`) {
        insertionIndex = i + 1
      } else {
        break
      }
    }
    setMissingData(missingData)
    // Merge existing data with the newly added missing weeks
    const mergedData = [
      ...data.slice(0, insertionIndex),
      ...newData,
      ...data.slice(insertionIndex),
    ].sort((a, b) => {
      const aYear = parseInt(a.week.substring(0, 4), 10)
      const aWeek = parseInt(a.week.substring(a.week.indexOf('-W') + 2), 10)
      const bYear = parseInt(b.week.substring(0, 4), 10)
      const bWeek = parseInt(b.week.substring(b.week.indexOf('-W') + 2), 10)
      if (aYear !== bYear) {
        return aYear - bYear
      } else {
        return aWeek - bWeek
      }
    })

    return mergedData
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          API_URL + '/anomaly_detection_respiratory_syndrome_messages'
        )
        // setData(addMissingWeek(response.data))
        setData(
          addMissingWeek(response.data).filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            // return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
            return (
              (currentYear === startYear && currentWeek >= startWeekNumber) ||
              (currentYear === endYear && currentWeek <= 27) ||
              (currentYear > startYear && currentYear < endYear)
            );

          })
        )
        let endWeek = season?.endWeek?.split('-W')
        const alignedWeek =
          startYear == seasonReferenceYear
            ? selectedWeek
            : computeSelectedWeekForSeason(selectedWeek, season.startWeek)
        const selectWeek = alignedWeek.split('-W')
        const showArrow =
          (Number(selectWeek[0]) > startYear ||
            (Number(selectWeek[0]) === startYear && Number(selectWeek[1]) >= startWeekNumber)) &&
          (Number(selectWeek[0]) < Number(endWeek[0]) ||
            (Number(selectWeek[0]) === Number(endWeek[0]) &&
              Number(selectWeek[1]) <= Number(endWeek[1])))
        setShowArrow(showArrow)
        setSeasonSelectedWeek(alignedWeek);
      } catch (error) {
        console.error('Error fetching data:', error)
        // Handle error or set a default value for data
        setData([])
      }
    }

    fetchData()
  }, [selectedWeek])

  return (
    <>
      <div>
        <div className='card px-4'>
          <div className='card-body row justify-content-md-center'>
            {
              startYear == seasonReferenceYear &&
              <h2 className='text-primary p-2 bold'>
                {' '}
                Anomaly detection in the volume of messages reporting respiratory syndromes (France){' '}
                <OverlayTrigger
                  placement='right'
                  overlay={
                    <Tooltip id='logo-tooltip' style={{ width: '300px' }}>
                      <p>
                        Anomaly detection: the anomaly is based on the mean squared root between
                        predicted values and true ones. Using the validation data, an unsupervised
                        isolation forest model was used to measure from these mean squared root errors
                        to which extent a point can be considered an “outlier”. Three anomaly levels
                        were determined : 99th , 95th and 90th percentile of most anomalous points.
                        Each week, data from the year before is used to make a forecast and compared
                        to actual data, the mean squared error is calculated and used to compare to
                        the thresholds previously defined. Source: KapCode{' '}
                      </p>
                    </Tooltip>
                  }
                >
                  <img
                    //className='mb-1'
                    style={{ width: '12px' }}
                    alt='logo'
                    src={toAbsoluteUrl('/media/logos/ii.svg')}
                  />
                </OverlayTrigger>
              </h2>
            }
            {data.length > 0 ? (
              <>
                <Plot
                  data={[
                    {
                      type: 'scatter',
                      mode: 'lines',
                      marker: { color: '#70ad47' },
                      line: { dash: 'dot' },
                      name: '10th percentile treshold (low alert)',
                      x: data.map((entry) => entry.week),
                      y: data.map((entry) => entry.tenthPercentileThreshold),
                      hovertemplate:
                        '<b>%{fullData.name}</b><br>' +
                        'Week: %{x}<br>' +
                        '10th percentile treshold: %{y}<extra></extra>',
                    },
                    {
                      type: 'scatter',
                      mode: 'lines',
                      marker: { color: '#fd7e14' },
                      line: { dash: 'dot' },
                      name: '5th percentile treshold (medium alert)',
                      x: data.map((entry) => entry.week),
                      y: data.map((entry) => entry.fifthPercentileThreshold),
                      hovertemplate:
                        '<b>%{fullData.name}</b><br>' +
                        'Week: %{x}<br>' +
                        '5th percentile treshold: %{y}<extra></extra>',
                    },
                    {
                      type: 'scatter',
                      mode: 'lines',
                      line: { dash: 'dot' },
                      marker: { color: '#E21A1A' },
                      name: '1st percentile treshold (High alert)',
                      x: data.map((entry) => entry.week),
                      y: data.map((entry) => entry.firstPercentileThreshold),
                      hovertemplate:
                        '<b>%{fullData.name}</b><br>' +
                        'Week: %{x}<br>' +
                        '1st percentile treshold: %{y}<extra></extra>',
                    },
                    {
                      type: 'scatter',
                      mode: 'lines',
                      marker: { color: '#203879' },
                      name: 'Weekly anomaly score',
                      x: data.map((entry) => entry.week),
                      y: data.map((entry) => entry.weeklyAnomalyScore),
                      hovertemplate:
                        '<b>%{fullData.name}</b><br>' +
                        'Week: %{x}<br>' +
                        '%{fullData.name}: %{y}<extra></extra>',
                    },
                    {
                      type: 'scatter',
                      mode: 'markers',
                      name: 'Missing Data',
                      x: missingData.map((entry) => entry.week),
                      y: missingData.map(() => 0),
                      marker: { color: '#000000' },
                      hovertemplate:
                        '<b>%{fullData.name}</b><br>' +
                        'Week: %{x}<br>' +
                        '%{fullData.name}: %{y}<extra></extra>',
                    },
                  ]}
                  layout={{
                    title: 'Anomaly detection',
                    annotations: [
                      {
                        xref: 'paper',
                        yref: 'paper',
                        x: -0.035,
                        xanchor: 'right',
                        y: 0.5,
                        yanchor: 'bottom',
                        text: '#',
                        font: {
                          size: 25,

                          //color: '#203879',
                        },
                        showarrow: false,
                      },
                      {
                        x: startYear == 2025
                          ? selectedWeek : seasonSelectedWeek,
                        y: 0,
                        xref: 'x',
                        yref: 'y',
                        text: showArrow ? 'Selected Week' : '',
                        font: {
                          family: 'Courier New, monospace',
                          size: 16,
                          color: '#CC1B1B',
                        },
                        arrowcolor: '#CC1B1B',
                        showarrow: showArrow,
                        arrowhead: 7,
                        ax: 0,
                        ay: -220,
                      },
                      // {
                      //   xref: 'paper',
                      //   yref: 'paper',
                      //   x: 1.04,
                      //   xanchor: 'left',
                      //   y: 0.5,
                      //   yanchor: 'bottom',
                      //   text: '%',
                      //   font: {
                      //     size: 25,

                      //     //color: '#1F77B4',
                      //   },
                      //   showarrow: false,
                      // },
                    ],
                    //title: 'Anomaly detection',
                    xaxis: { title: 'Week', tickangle: -45 },
                    yaxis: {
                      // title: {
                      //   text: '#',
                      //   font: {
                      //     size: 25,
                      //     // color: '#1F77B4',
                      //   },
                      // },
                    },
                    legend: {
                      x: 0,
                      y: -0.4, // Adjust the value to position the legend as per your requirement
                      orientation: 'h', // 'h' for horizontal, 'v' for vertical
                    },
                  }}
                  config={{ displayModeBar: true, displaylogo: false, responsive: true }}
                />
              </>
            ) : (
              <p>Loading data...</p>
            )}

            <div className="d-flex justify-content-between align-items-center">
              <p className='card-text source'>
                <i>
                  Source:
                  <a className='fw-bold' href='https://www.kapcode.fr/'>
                    {' '}
                    KapCode
                  </a>
                </i>
              </p>
              {
                startYear == seasonReferenceYear &&
                <SourceLink onClick={onClick} />
              }
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default AnomalyDetectionGraph
